import { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import * as Tone from "tone";
import { MdPlayArrow, MdPause, MdSkipNext, MdBugReport, MdClose } from "react-icons/md";
import { useSamples } from "./useSamples";
import { useTransport } from "./useTransport";
import { useHarmony, HarmonyConfig } from "./useHarmony";
import { useMelody, MelodyConfig } from "./useMelody";
import { pickSessionPersonality } from "./sessionPersonality";
import { useBass } from "./useBass";
import { useDrums } from "./useDrums";
import { useDSP } from "./useDSP";
import { useAnalyser } from "./useAnalyser";
import { OrbVisualiser } from "./OrbVisualiser";
import { NOTE_NAMES, PROGRESSION_LABELS } from "./harmonyEngine";

const DEBUG_TAPS = 5;
const TAP_WINDOW_MS = 2000;

// Root note → index 0–11, used to spread hue within a mood zone
const NOTE_OFFSETS: Record<string, number> = {
  C: 0, Db: 1, D: 2, Eb: 3, E: 4, F: 5,
  "F#": 6, G: 7, Ab: 8, A: 9, Bb: 10, B: 11,
};

interface ChordColor { hue: number; sat: number; lit: number; }

function chordColor(name: string | null): ChordColor {
  if (!name) return { hue: 240, sat: 60, lit: 60 };
  const root = name.match(/^[A-G][b#]?/)?.[0] ?? "C";
  const offset = NOTE_OFFSETS[root] ?? 0;
  if (/ø/.test(name)) {
    // Half-dim → muted blue-violet
    return { hue: Math.round(250 + offset * 2.5), sat: 45, lit: 58 };
  }
  if (/min/.test(name)) {
    // Minor → cool (teal → indigo → violet: 185–265)
    return { hue: Math.round(185 + offset * (80 / 11)), sat: 65, lit: 60 };
  }
  if (!/maj/.test(name)) {
    // Dominant (7, 9) → warm amber (30–90)
    return { hue: Math.round(30 + offset * (60 / 11)), sat: 72, lit: 63 };
  }
  // Major → warm gold/amber (25–75)
  return { hue: Math.round(25 + offset * (50 / 11)), sat: 70, lit: 62 };
}

// Arc geometry — SVG viewport 200×200, center (100,100)
const CX = 100, CY = 100, ARC_R = 75;
const START_ANG = 225; // degrees clockwise from 12-o'clock (7-o'clock position)
const SWEEP = 270;     // gap sits at the bottom center

function toXY(angleDeg: number, r: number): [number, number] {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
}

function arcPath(startDeg: number, sweepDeg: number, r: number): string {
  if (sweepDeg <= 0) return "";
  const [sx, sy] = toXY(startDeg, r);
  const [ex, ey] = toXY(startDeg + sweepDeg, r);
  return `M ${sx} ${sy} A ${r} ${r} 0 ${sweepDeg > 180 ? 1 : 0} 1 ${ex} ${ey}`;
}

const DEFAULT_VOL = 0.8;

// Gate: dev env or ?debug=1 query param unlocks immediately (tap gesture still works in prod)
const isDebugEnv =
  process.env.NODE_ENV === "development" ||
  new URLSearchParams(window.location.search).has("debug");

interface DebugConfig {
  melodyEnabled: boolean;
  drumsEnabled: boolean;
  bassEnabled: boolean;
  chordsEnabled: boolean;
  bpm: number;
  midiMin: number;
  midiMax: number;
  progressionLock: number | null;
  keyRootOverride: number | null;
  guitarChance: number;
  instrumentOverride: "piano" | "guitar" | null;
}

export default function LofiGenerator() {
  // Config refs read by audio callbacks — updated synchronously with state
  const melodyConfigRef = useRef<MelodyConfig>({
    enabled: true,
    midiMin: 65,
    midiMax: 79,
  });
  const bassConfigRef = useRef({ enabled: false });
  const drumsConfigRef = useRef({ enabled: false, hihatEnabled: false });
  const harmonyConfigRef = useRef<HarmonyConfig>({
    enabled: false,
    progressionLock: null,
    keyRootOverride: null,
    progressionPool: null,
    registerOffset: 0,
    rollChance: 0.5,
    passingChordChance: 0.5,
  });
  const instrumentConfigRef = useRef({ guitarChance: 0.3, override: null as "piano" | "guitar" | null });
  const chordSamplerRef = useRef<Tone.Sampler | null>(null);   // always piano
  const melodySamplerRef = useRef<Tone.Sampler | null>(null);  // piano or guitar
  const [activeInstrument, setActiveInstrument] = useState<"piano" | "guitar">("piano");

  const { loaded, samples } = useSamples();
  const { playing, toggle, bpm: initialBpm, setBpm, restartTransport } = useTransport();
  const { chord, chordContextRef, skip } = useHarmony(samples, harmonyConfigRef, chordSamplerRef);
  useMelody(samples, chordContextRef, melodyConfigRef, melodySamplerRef);
  useBass(samples, chordContextRef, bassConfigRef);
  const { switchPattern } = useDrums(samples, drumsConfigRef);
  useDSP(samples);
  const analyserRef = useAnalyser();

  const [volume, setVolume] = useState(DEFAULT_VOL);
  const [debugUnlocked, setDebugUnlocked] = useState(isDebugEnv);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugConfig, setDebugConfig] = useState<DebugConfig>(() => ({
    melodyEnabled: true,
    drumsEnabled: false,
    bassEnabled: false,
    chordsEnabled: false,
    bpm: initialBpm,
    midiMin: 65,
    midiMax: 79,
    progressionLock: null,
    keyRootOverride: null,
    guitarChance: 0.3,
    instrumentOverride: null,
  }));

  const tapTimestamps = useRef<number[]>([]);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);

  const handleTitleTap = useCallback(() => {
    const now = Date.now();
    tapTimestamps.current = [...tapTimestamps.current, now].filter(
      (t) => now - t < TAP_WINDOW_MS
    );
    if (tapTimestamps.current.length >= DEBUG_TAPS) {
      tapTimestamps.current = [];
      setDebugUnlocked(true);
    }
  }, []);

  useEffect(() => {
    Tone.Destination.volume.value = Tone.gainToDb(DEFAULT_VOL);
  }, []);

  // Wire chord sampler — always piano regardless of active melody instrument
  useEffect(() => {
    if (samples) chordSamplerRef.current = samples.piano;
  }, [samples]);

  // Layered intro — enable layers progressively based on bar count
  useEffect(() => {
    if (!samples) return;
    const id = Tone.Transport.scheduleRepeat(() => {
      const bar = parseInt((Tone.Transport.position as string).split(":")[0]);
      drumsConfigRef.current   = { enabled: true,      hihatEnabled: bar >= 6 };
      bassConfigRef.current    = { enabled: bar >= 6 };
      harmonyConfigRef.current = { ...harmonyConfigRef.current, enabled: true };
      melodyConfigRef.current  = { ...melodyConfigRef.current,  enabled: bar >= 6 };
    }, "1m");
    return () => { Tone.Transport.clear(id); };
  }, [samples]);

  function applyVolume(v: number) {
    const clamped = Math.max(0, Math.min(1, v));
    setVolume(clamped);
    Tone.Destination.volume.rampTo(Tone.gainToDb(clamped || 0.0001), 0.05);
  }

  async function handlePlayPause() {
    if (playing) {
      if (volume < 0.01) { await toggle(); return; }
      const FADE_OUT = 0.3;
      Tone.Destination.volume.rampTo(-60, FADE_OUT);
      setTimeout(async () => {
        await toggle();
        Tone.Destination.volume.value = Tone.gainToDb(volume);
      }, FADE_OUT * 1000);
    } else {
      applyPersonality();
      resetPhase();
      skip();          // re-init harmony state with new personality values
      pickInstrument();
      Tone.Destination.volume.value = -60;
      await toggle();
      Tone.Destination.volume.rampTo(Tone.gainToDb(volume || 0.0001), 0.4);
    }
  }

  function resetPhase() {
    drumsConfigRef.current   = { enabled: true, hihatEnabled: false };
    bassConfigRef.current    = { enabled: false };
    harmonyConfigRef.current = { ...harmonyConfigRef.current, enabled: true };
    melodyConfigRef.current  = { ...melodyConfigRef.current, enabled: false };
  }

  function applyPersonality() {
    const p = pickSessionPersonality();
    setBpm(p.bpm);
    harmonyConfigRef.current = {
      ...harmonyConfigRef.current,
      progressionPool: p.progressionPool,
      registerOffset: p.registerOffset,
      rollChance: p.rollChance,
      passingChordChance: p.passingChordChance,
    };
    melodyConfigRef.current = { ...melodyConfigRef.current, phrasePool: p.melodyPhrasePool };
    instrumentConfigRef.current = { ...instrumentConfigRef.current, guitarChance: p.guitarBias };
  }

  function pickInstrument() {
    const cfg = instrumentConfigRef.current;
    const choice = cfg.override ?? (Math.random() < cfg.guitarChance ? "guitar" : "piano");
    if (samples) melodySamplerRef.current = samples[choice];
    setActiveInstrument(choice);
    melodyConfigRef.current = { ...melodyConfigRef.current, guitarMode: choice === "guitar" };
  }

  function handleSkip() {
    if (!playing || volume < 0.01) {
      applyPersonality();
      resetPhase();
      skip();
      switchPattern();
      pickInstrument();
      return;
    }
    const FADE_OUT = 0.2;
    const FADE_IN  = 0.25;
    Tone.Destination.volume.rampTo(-60, FADE_OUT);
    setTimeout(() => {
      applyPersonality();
      resetPhase();
      restartTransport();
      skip();
      switchPattern();
      pickInstrument();
      Tone.Destination.volume.rampTo(Tone.gainToDb(volume), FADE_IN);
    }, FADE_OUT * 1000);
  }

  function volumeFromPointer(clientX: number, clientY: number): number | null {
    if (!svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const scale = 200 / rect.width;
    const dx = (clientX - (rect.left + rect.width / 2)) * scale;
    const dy = (clientY - (rect.top + rect.height / 2)) * scale;
    if (Math.abs(Math.sqrt(dx * dx + dy * dy) - ARC_R) > 22) return null;
    const clockDeg = ((Math.atan2(dy, dx) * 180) / Math.PI + 90 + 360) % 360;
    const rel = (clockDeg - START_ANG + 360) % 360;
    if (rel > SWEEP) return null;
    return rel / SWEEP;
  }

  function handlePointerDown(e: React.PointerEvent<SVGSVGElement>) {
    const vol = volumeFromPointer(e.clientX, e.clientY);
    if (vol === null) return;
    dragging.current = true;
    svgRef.current!.setPointerCapture(e.pointerId);
    applyVolume(vol);
  }

  function handlePointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (!dragging.current) return;
    const vol = volumeFromPointer(e.clientX, e.clientY);
    if (vol !== null) applyVolume(vol);
  }

  function updateDebugConfig(patch: Partial<DebugConfig>) {
    setDebugConfig((prev) => {
      const next = { ...prev, ...patch };
      // Sync audio refs immediately
      melodyConfigRef.current = {
        ...melodyConfigRef.current,
        enabled: next.melodyEnabled,
        midiMin: next.midiMin,
        midiMax: next.midiMax,
      };
      bassConfigRef.current = { enabled: next.bassEnabled };
      drumsConfigRef.current = { enabled: next.drumsEnabled, hihatEnabled: drumsConfigRef.current.hihatEnabled };
      harmonyConfigRef.current = {
        ...harmonyConfigRef.current,
        enabled: next.chordsEnabled,
        progressionLock: next.progressionLock,
        keyRootOverride: next.keyRootOverride,
      };
      instrumentConfigRef.current = {
        guitarChance: next.guitarChance,
        override: next.instrumentOverride,
      };
      if ("bpm" in patch) {
        Tone.Transport.bpm.rampTo(next.bpm, 0.1);
      }
      return next;
    });
  }

  const { hue, sat, lit } = chordColor(chord?.name ?? null);

  return (
    <div className="fixed h-full w-full bg-[#09090b] flex flex-col items-center justify-center gap-2">
      <style>{`
        @keyframes orb-breathe {
          0%, 100% { transform: scale(1);    opacity: 0.38; }
          50%       { transform: scale(1.22); opacity: 0.62; }
        }
      `}</style>
      <Helmet>
        <title>lofi generator — ponchotang</title>
        <meta name="description" content="lofi music generator" />
      </Helmet>

      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <p
          className="text-zinc-400 text-xs tracking-widest uppercase select-none cursor-default"
          onClick={handleTitleTap}
        >
          lofi generator
        </p>
      </div>

      {/* Arc + play/pause + skip */}
      <div className="relative">
        {/* Orb */}
        <div
          className="absolute inset-0 rounded-full blur-3xl pointer-events-none"
          style={{
            background: `radial-gradient(circle, hsl(${hue}, ${sat}%, ${Math.round(lit * 0.6)}%) 0%, transparent 70%)`,
            animation: playing ? "orb-breathe 3s ease-in-out infinite" : "none",
            opacity: playing ? undefined : 0.15,
            transition: "opacity 1.2s ease, background 1.5s ease",
          }}
        />

        {/* Radial frequency visualiser */}
        <OrbVisualiser analyserRef={analyserRef} playing={playing} hue={hue} sat={sat} lit={lit} />

        {/* Volume arc */}
        <svg
          ref={svgRef}
          width={200}
          height={200}
          viewBox="0 0 200 200"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={() => { dragging.current = false; }}
          onPointerCancel={() => { dragging.current = false; }}
          style={{ touchAction: "none", cursor: "default" }}
        >
          {/* Track */}
          <path
            d={arcPath(START_ANG, SWEEP, ARC_R)}
            fill="none"
            stroke="#27272a"
            strokeWidth={5}
            strokeLinecap="round"
          />
          {/* Fill */}
          {volume > 0.005 && (
            <path
              d={arcPath(START_ANG, volume * SWEEP, ARC_R)}
              fill="none"
              stroke={`hsl(${hue}, ${sat - 10}%, ${lit - 2}%)`}
              strokeWidth={5}
              strokeLinecap="round"
              style={{ transition: "stroke 1.5s ease" }}
            />
          )}
        </svg>

        {/* Play / pause */}
        <button
          onClick={handlePlayPause}
          disabled={!loaded}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60px] h-[60px] rounded-full bg-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-zinc-700 hover:text-white active:scale-95 disabled:opacity-30 transition-all"
        >
          {loaded
            ? (playing ? <MdPause size={26} /> : <MdPlayArrow size={26} />)
            : <span className="w-3 h-3 rounded-full bg-zinc-700 animate-pulse" />
          }
        </button>

        {/* Skip */}
        <button
          onClick={handleSkip}
          className="absolute bottom-[18px] left-1/2 -translate-x-1/2 text-zinc-600 hover:text-zinc-400 transition-colors"
          title="new progression"
        >
          <MdSkipNext size={20} />
        </button>
      </div>

      {/* Debug button */}
      {debugUnlocked && (
        <button
          onClick={() => setDebugOpen((o) => !o)}
          className="fixed bottom-4 right-4 w-9 h-9 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-500 hover:text-amber-400 hover:border-amber-400/40 transition-colors"
          title="debug panel"
        >
          <MdBugReport size={18} />
        </button>
      )}

      {/* Debug panel */}
      {debugOpen && (
        <div className="fixed bottom-16 right-4 w-72 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl text-xs text-zinc-400 overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <span className="text-zinc-300 font-medium tracking-widest uppercase text-[10px]">Debug</span>
            <button onClick={() => setDebugOpen(false)} className="text-zinc-600 hover:text-zinc-400 transition-colors">
              <MdClose size={14} />
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {/* Layer toggles */}
            <div className="px-4 py-3 border-b border-zinc-800">
              <p className="text-[10px] tracking-widest uppercase text-zinc-600 mb-2">Layers</p>
              <div className="grid grid-cols-4 gap-1.5">
                {(
                  [
                    ["melody",  "melodyEnabled"]  ,
                    ["drums",   "drumsEnabled"]   ,
                    ["bass",    "bassEnabled"]    ,
                    ["chords",  "chordsEnabled"]  ,
                  ] as [string, keyof DebugConfig][]
                ).map(([label, key]) => (
                  <button
                    key={key}
                    onClick={() => updateDebugConfig({ [key]: !debugConfig[key] } as Partial<DebugConfig>)}
                    className={`py-1 rounded text-[10px] font-medium transition-colors ${
                      debugConfig[key]
                        ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                        : "bg-zinc-800 text-zinc-600 border border-zinc-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Transport */}
            <div className="px-4 py-3 border-b border-zinc-800">
              <p className="text-[10px] tracking-widest uppercase text-zinc-600 mb-2">Transport</p>
              <label className="flex items-center justify-between gap-2">
                <span>BPM</span>
                <div className="flex items-center gap-2 flex-1 justify-end">
                  <input
                    type="range"
                    min={60} max={130} step={1}
                    value={debugConfig.bpm}
                    onChange={(e) => updateDebugConfig({ bpm: Number(e.target.value) })}
                    className="w-24 accent-amber-400"
                  />
                  <span className="text-zinc-300 w-7 text-right">{debugConfig.bpm}</span>
                </div>
              </label>
            </div>

            {/* Melody params */}
            <div className="px-4 py-3 border-b border-zinc-800 space-y-2">
              <p className="text-[10px] tracking-widest uppercase text-zinc-600">Melody</p>
              <div className="flex items-center justify-between gap-2">
                <span>MIDI range</span>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={36} max={debugConfig.midiMax - 1}
                    value={debugConfig.midiMin}
                    onChange={(e) => updateDebugConfig({ midiMin: Number(e.target.value) })}
                    className="w-12 bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-zinc-300 text-xs text-center"
                  />
                  <span>–</span>
                  <input
                    type="number"
                    min={debugConfig.midiMin + 1} max={108}
                    value={debugConfig.midiMax}
                    onChange={(e) => updateDebugConfig({ midiMax: Number(e.target.value) })}
                    className="w-12 bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-zinc-300 text-xs text-center"
                  />
                </div>
              </div>
            </div>

            {/* Harmony params */}
            <div className="px-4 py-3 border-b border-zinc-800 space-y-2">
              <p className="text-[10px] tracking-widest uppercase text-zinc-600">Harmony</p>
              <div className="flex items-center justify-between">
                <span>Key root</span>
                <select
                  value={debugConfig.keyRootOverride ?? -1}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    updateDebugConfig({ keyRootOverride: v === -1 ? null : v });
                  }}
                  className="bg-zinc-800 border border-zinc-700 rounded px-2 py-0.5 text-zinc-300 text-xs"
                >
                  <option value={-1}>random</option>
                  {NOTE_NAMES.map((n, i) => (
                    <option key={n} value={i}>{n}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between">
                <span>Progression</span>
                <select
                  value={debugConfig.progressionLock ?? -1}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    updateDebugConfig({ progressionLock: v === -1 ? null : v });
                  }}
                  className="bg-zinc-800 border border-zinc-700 rounded px-2 py-0.5 text-zinc-300 text-xs max-w-[130px]"
                >
                  <option value={-1}>random</option>
                  {PROGRESSION_LABELS.map((lbl, i) => (
                    <option key={i} value={i}>{i}: {lbl}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Instrument */}
            <div className="px-4 py-3 border-b border-zinc-800 space-y-2">
              <p className="text-[10px] tracking-widest uppercase text-zinc-600">Instrument</p>
              <label className="flex items-center justify-between gap-2">
                <span>Guitar chance</span>
                <div className="flex items-center gap-2 flex-1 justify-end">
                  <input
                    type="range"
                    min={0} max={1} step={0.01}
                    value={debugConfig.guitarChance}
                    onChange={(e) => updateDebugConfig({ guitarChance: Number(e.target.value) })}
                    className="w-24 accent-amber-400"
                  />
                  <span className="text-zinc-300 w-7 text-right">{Math.round(debugConfig.guitarChance * 100)}%</span>
                </div>
              </label>
              <div className="flex items-center justify-between">
                <span>Override</span>
                <div className="flex gap-1">
                  {([null, "piano", "guitar"] as const).map((m) => (
                    <button
                      key={m ?? "auto"}
                      onClick={() => updateDebugConfig({ instrumentOverride: m })}
                      className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                        debugConfig.instrumentOverride === m
                          ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                          : "bg-zinc-800 text-zinc-600 border border-zinc-700"
                      }`}
                    >
                      {m ?? "auto"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="px-4 py-3 space-y-1.5">
              <p className="text-[10px] tracking-widest uppercase text-zinc-600 mb-2">Status</p>
              {[
                ["chord",      chord?.name ?? "—"],
                ["instrument", activeInstrument],
                ["volume",     `${Math.round(volume * 100)}%`],
                ["playing",    playing ? "yes" : "no"],
                ["samples",    loaded ? "ready" : "loading"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span>{k}</span>
                  <span className="text-zinc-300">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
